import React, {useState} from 'react';
import cx from 'classnames';
import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  UniqueIdentifier
} from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  createTabAway,
  setTabbedAway,
  TabThroughKeyboardSensor,
  withoutTabCancel
} from './TabThroughKeyboardSensor';
import type {TabThroughKeyboardSensorOptions} from './TabThroughKeyboardSensor';

interface DndContextWrapperProps {
  children: React.ReactNode;
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  /** Escape, Tab (see `keyboardSelects`), or a lost pointer ended the drag with nothing dropped. */
  onDragCancel?: (event: DragCancelEvent, info: {tabbedAway: boolean}) => void;
  /** Render the ghost shown during drag. Receives the active item's id. */
  renderOverlay?: (activeId: UniqueIdentifier) => React.ReactNode;
  /** Screen-reader announcements and instructions for the drag, spoken through DndContext's live region. */
  accessibility?: React.ComponentProps<typeof DndContext>['accessibility'];
  /**
   * A keyboard pickup is a selection that outlives the drag: Tab cancels the drag, says nothing,
   * and moves focus on, so Enter on the next control can place the item.
   */
  keyboardSelects?: boolean;
}

/** Whether the drag was picked up with Enter or Space, not a pointer. */
export const isKeyboardPickup = ({activatorEvent}: {activatorEvent: Event | null}) =>
  activatorEvent instanceof KeyboardEvent;

/**
 * Tailwind classes for the element the user actually grabs and drags
 * Apply these to the individual draggable node (the `div` / `Chip` that carries
 * `ref={setNodeRef}` and `{...listeners}`)
 *
 * @param isDragging Pass `!!transform` (from `useDraggable`) to switch cursor.
 * @param isFrozen Swaps the grab cursor and hover/pressed rings for a default cursor, for a chip that can't be dragged.
 * @param isAnnotatable With `isFrozen`, leaves the cursor to the chip's annotation region, which shows the highlighter.
 */
export const getDraggableClasses = (isDragging: boolean, isFrozen = false, isAnnotatable = false) =>
  [
    'inline-flex',
    'rounded',
    ...(isFrozen
      ? isAnnotatable
        ? []
        : ['cursor-default!']
      : [
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
          isDragging ? '[&_*]:cursor-grabbing' : '[&_*]:cursor-grab',
          'hover:ring-2',
          'hover:ring-border-bold',
          'hover:background-subtle-hover!',
          'active:ring-2',
          'active:ring-border-selected'
        ])
  ].join(' ');

/**
 * Shared wrapper around DndContext that standardises sensor configuration
 */
const DndContextWrapper = ({
  children,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragCancel,
  renderOverlay,
  accessibility,
  keyboardSelects = false
}: DndContextWrapperProps) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [tabAway] = useState(createTabAway);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const tabThroughKeyboardSensor = useSensor<TabThroughKeyboardSensorOptions>(
    TabThroughKeyboardSensor,
    {
      onTab: () => setTabbedAway(tabAway, true)
    }
  );
  const sensors = useSensors(
    pointerSensor,
    keyboardSelects ? tabThroughKeyboardSensor : keyboardSensor
  );

  const announcements =
    accessibility?.announcements && withoutTabCancel(accessibility.announcements, tabAway);

  const handleDragStart = (event: DragStartEvent) => {
    setTabbedAway(tabAway, false);
    setActiveId(event.active.id);
    onDragStart?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    onDragEnd(event);
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    setActiveId(null);
    onDragCancel?.(event, {tabbedAway: tabAway.tabbedAway});
  };

  return (
    <div data-dnd-context className={cx({'**:cursor-grabbing!': activeId !== null})}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={onDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        accessibility={announcements ? {...accessibility, announcements} : accessibility}
      >
        {children}

        {renderOverlay && (
          <DragOverlay dropAnimation={null}>
            {activeId && (
              <div className='inline-flex h-fit w-fit origin-top-left scale-[0.85] rotate-[-7deg] opacity-90'>
                {renderOverlay(activeId)}
              </div>
            )}
          </DragOverlay>
        )}
      </DndContext>
    </div>
  );
};

export default DndContextWrapper;
